import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components/Button';
import { TranscriptEntry, VoiceAgentError } from '../../types';
import { styleSheet } from './styles';

interface VoiceAgentInterfaceProps {
	isConnected: boolean;
	isConnecting: boolean;
	transcript: TranscriptEntry[];
	onDisconnect: () => void;
	onClearTranscript: () => void;
	onDownloadTranscript: () => void;
	error?: VoiceAgentError;
}

export const VoiceAgentInterface: React.FC<VoiceAgentInterfaceProps> = ({
	isConnected,
	isConnecting,
	transcript,
	onDisconnect,
	onClearTranscript,
	onDownloadTranscript,
	error,
}) => {
	const transcriptContentRef = React.useRef<HTMLDivElement>(null);

	// Auto-scroll to bottom when new messages are added
	React.useEffect(() => {
		if (transcriptContentRef.current && transcript.length > 0) {
			transcriptContentRef.current.scrollTo({
				top: transcriptContentRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	}, [transcript]);
	const getStatusText = () => {
		if (error) return 'Connection Error';
		if (isConnecting) return 'Connecting...';
		if (isConnected) return 'Connected - Start Speaking!';
		return 'Disconnected';
	};

	const getStatusClassName = () => {
		if (error) return styleSheet.statusError;
		if (isConnecting) return styleSheet.statusConnecting;
		if (isConnected) return styleSheet.statusConnected;
		return styleSheet.statusDisconnected;
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.statusSection)}>
				<div className={css(styleSheet.status, getStatusClassName())}>
					<div className={css(styleSheet.statusIndicator)} />
					<span className={css(styleSheet.statusText)}>{getStatusText()}</span>
				</div>

				{error && <div className={css(styleSheet.errorMessage)}>{error.message}</div>}
			</div>

			{isConnected && (
				<div className={css(styleSheet.instructionsSection)}>
					<p className={css(styleSheet.instructions)}>
						🎯 <strong>You&apos;re now connected to an AI prospect!</strong> Start your sales conversation. The AI will
						respond as a realistic prospect based on the lead data.
					</p>
				</div>
			)}

			<div className={css(styleSheet.transcriptSection)}>
				<div className={css(styleSheet.transcriptHeader)}>
					<h3 className={css(styleSheet.transcriptTitle)}>Call Transcript</h3>
					{transcript.length > 0 && (
						<div className={css(styleSheet.transcriptActions)}>
							<Button
								kind='secondary'
								size='small'
								onClick={onClearTranscript}
								className={css(styleSheet.transcriptButton)}
							>
								Clear
							</Button>
							<Button
								kind='secondary'
								size='small'
								onClick={onDownloadTranscript}
								className={css(styleSheet.transcriptButton)}
							>
								Download
							</Button>
						</div>
					)}
				</div>

				<div ref={transcriptContentRef} className={css(styleSheet.transcriptContent)}>
					{transcript.length === 0 ? (
						<div className={css(styleSheet.transcriptPlaceholder)}>
							{isConnected
								? 'Transcript will appear here when the conversation starts...'
								: 'Connect to start the voice session and see the transcript here.'}
						</div>
					) : (
						transcript.map((entry, index) => (
							<div
								key={index}
								className={css(
									styleSheet.transcriptEntry,
									entry.speaker === 'Sales Rep'
										? styleSheet.transcriptEntrySalesRep
										: styleSheet.transcriptEntryProspect
								)}
							>
								<div className={css(styleSheet.transcriptSpeaker)}>{entry.speaker}</div>
								<div className={css(styleSheet.transcriptTimestamp)}>{entry.timestamp.toLocaleTimeString()}</div>
								<div className={css(styleSheet.transcriptMessage)}>{entry.message}</div>
							</div>
						))
					)}
				</div>
			</div>

			{(isConnected || isConnecting) && (
				<div className={css(styleSheet.controlSection)}>
					<Button kind='primary' onClick={onDisconnect} className={css(styleSheet.disconnectButton)}>
						End Session
					</Button>
				</div>
			)}
		</div>
	);
};
